import React, { useState } from 'react';
import {
  Card,
  Button,
  Popconfirm,
  Popover,
  Checkbox,
  message,
  Tooltip,
} from 'antd';
import { EyeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import type { ImageItem } from '../types';
import dayjs from 'dayjs';

interface ImageGridProps {
  images: ImageItem[];
  selectedImages: number[];
  onImageSelect: (imageId: number) => void;
  onImageDelete: (imageId: number) => void;
  onImageView: (image: ImageItem) => void;
  onImageUpdate?: (imageId: number, data: Partial<ImageItem>) => void;
  selectable?: boolean;
  onSelectionChange?: (selectedIds: number[]) => void;
  maxCompareSelection?: number;
}

export const ImageGrid: React.FC<ImageGridProps> = ({
  images,
  selectedImages,
  onImageSelect,
  onImageDelete,
  onImageView,
  onImageUpdate,
  selectable = false,
  onSelectionChange,
  maxCompareSelection = 6,
}) => {
  const [hoveredImage, setHoveredImage] = useState<number | null>(null);
  const [updateImageId, setUpdateImageId] = useState<number | null>(null);

  const handleImageClick = (image: ImageItem) => {
    if (selectable) {
      const isSelected = selectedImages.includes(image.id);
      const newSelection = isSelected
        ? selectedImages.filter(id => id !== image.id)
        : selectedImages.length < maxCompareSelection
          ? [...selectedImages, image.id]
          : selectedImages;
      if (!isSelected && selectedImages.length >= maxCompareSelection) {
        message.info(
          `You can select up to ${maxCompareSelection} images for comparison`
        );
      }
      onSelectionChange?.(newSelection);
    } else {
      onImageSelect(image.id);
    }
  };

  const handleCategoryUpdate = (imageId: number, category: string) => {
    if (onImageUpdate) {
      onImageUpdate(imageId, { category });
      setUpdateImageId(null);
    }
  };

  const categories = ['GENERAL', 'PRIVATE', 'LHR'];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4 overflow-y-auto max-h-full pb-2 px-1">
      {images.map((image: ImageItem) => {
        const isSelected = selectedImages.includes(image.id);
        const isHovered = hoveredImage === image.id;

        return (
          <Card
            key={image.id}
            className={`relative transition-all duration-200 ${
              isSelected ? 'ring-2 ring-blue-500 bg-blue-50' : ''
            }`}
            style={{
              aspectRatio: '1',
              minHeight: '150px',
            }}
            bodyStyle={{
              padding: 0,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
            onMouseEnter={() => setHoveredImage(image.id)}
            onMouseLeave={() => {
              setHoveredImage(null);
              setUpdateImageId(null);
            }}
            onClick={() => handleImageClick(image)}
          >
            {selectable && (
              <div
                className={`absolute left-2 top-2 z-20 transition-opacity duration-200 ${
                  isHovered || isSelected ? 'opacity-100' : 'opacity-0'
                }`}
                onClick={e => e.stopPropagation()}
              >
                <Checkbox
                  checked={isSelected}
                  onChange={e => {
                    e.stopPropagation();
                    const checked = e.target.checked;
                    const newSelection = checked
                      ? selectedImages.length < maxCompareSelection &&
                        !selectedImages.includes(image.id)
                        ? [...selectedImages, image.id]
                        : selectedImages
                      : selectedImages.filter(id => id !== image.id);
                    if (
                      checked &&
                      selectedImages.length >= maxCompareSelection &&
                      !selectedImages.includes(image.id)
                    ) {
                      message.info(
                        `You can select up to ${maxCompareSelection} images for comparison`
                      );
                    }
                    onSelectionChange?.(newSelection);
                  }}
                />
              </div>
            )}

            <div className="relative flex-1 overflow-hidden">
              <img
                src={image.thumbnail_url || image.url}
                alt={image.name}
                className="w-full h-full object-cover transition-transform duration-200 hover:scale-105"
              />

              {isHovered && (
                <div className="hidden lg:flex absolute inset-0 items-center justify-center gap-3 bg-black/50">
                  <Tooltip title="View Image">
                    <Button
                      type="text"
                      icon={<EyeOutlined />}
                      onClick={e => {
                        e.stopPropagation();
                        onImageView(image);
                      }}
                      className="text-white hover:bg-white/20"
                    />
                  </Tooltip>

                  <Popover
                    open={updateImageId === image.id}
                    onOpenChange={open =>
                      setUpdateImageId(open ? image.id : null)
                    }
                    title="Update Image Category"
                    content={
                      <div className="w-72">
                        <p className="mb-4 text-sm text-gray-600">
                          Change the category for "{image.name}"
                        </p>
                        <div className="space-y-2">
                          {categories.map(category => (
                            <Button
                              key={category}
                              type={
                                image.category === category
                                  ? 'primary'
                                  : 'default'
                              }
                              block
                              onClick={e => {
                                e.stopPropagation();
                                if (image.category !== category) {
                                  handleCategoryUpdate(image.id, category);
                                }
                              }}
                            >
                              {category}
                            </Button>
                          ))}
                        </div>
                      </div>
                    }
                    trigger="click"
                  >
                    <Tooltip title="Edit Category">
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={e => {
                          e.stopPropagation();
                          setUpdateImageId(image.id);
                        }}
                        className="text-white hover:bg-white/20"
                      />
                    </Tooltip>
                  </Popover>

                  <Popconfirm
                    title="Delete Image"
                    description={`Are you sure you want to delete "${image.name}"? This action cannot be undone.`}
                    onConfirm={e => {
                      e?.stopPropagation();
                      onImageDelete(image.id);
                    }}
                    onCancel={e => e?.stopPropagation()}
                    okText="Delete"
                    cancelText="Cancel"
                    okType="danger"
                  >
                    <Tooltip title="Delete Image">
                      <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={e => e.stopPropagation()}
                        className="text-white hover:bg-white/20"
                        danger
                      />
                    </Tooltip>
                  </Popconfirm>
                </div>
              )}

              <div className="lg:hidden absolute bottom-0 left-0 right-0 flex justify-center gap-1 p-1 bg-gradient-to-t from-black/70 to-transparent">
                <Tooltip title="View Image">
                  <Button
                    type="text"
                    icon={<EyeOutlined />}
                    onClick={e => {
                      e.stopPropagation();
                      onImageView(image);
                    }}
                    size="small"
                    className="text-white hover:bg-white/20 bg-black/50"
                    style={{ height: '24px', width: '24px', minWidth: '24px' }}
                  />
                </Tooltip>

                <Popover
                  open={updateImageId === image.id}
                  onOpenChange={open =>
                    setUpdateImageId(open ? image.id : null)
                  }
                  title="Update Image Category"
                  content={
                    <div className="w-64">
                      <p className="mb-3 text-sm text-gray-600">
                        Change category for "{image.name}"
                      </p>
                      <div className="space-y-2">
                        {categories.map(category => (
                          <Button
                            key={category}
                            type={
                              image.category === category
                                ? 'primary'
                                : 'default'
                            }
                            block
                            onClick={e => {
                              e.stopPropagation();
                              if (image.category !== category) {
                                handleCategoryUpdate(image.id, category);
                              }
                            }}
                            size="small"
                          >
                            {category}
                          </Button>
                        ))}
                      </div>
                    </div>
                  }
                  trigger="click"
                >
                  <Tooltip title="Edit Category">
                    <Button
                      type="text"
                      icon={<EditOutlined />}
                      onClick={e => {
                        e.stopPropagation();
                        setUpdateImageId(image.id);
                      }}
                      size="small"
                      className="text-white hover:bg-white/20 bg-black/50"
                      style={{ height: '24px', width: '24px', minWidth: '24px' }}
                    />
                  </Tooltip>
                </Popover>

                <Popconfirm
                  title="Delete Image"
                  description={`Delete "${image.name}"?`}
                  onConfirm={e => {
                    e?.stopPropagation();
                    onImageDelete(image.id);
                  }}
                  onCancel={e => e?.stopPropagation()}
                  okText="Delete"
                  cancelText="Cancel"
                  okType="danger"
                >
                  <Tooltip title="Delete Image">
                    <Button
                      type="text"
                      icon={<DeleteOutlined />}
                      onClick={e => e.stopPropagation()}
                      size="small"
                      className="text-white hover:bg-white/20 bg-black/50"
                      style={{ height: '24px', width: '24px', minWidth: '24px' }}
                    />
                  </Tooltip>
                </Popconfirm>
              </div>

              <div className="absolute bottom-8 left-0 right-0 p-2">
                <div className="text-xs font-medium text-white truncate">
                  {image.name}
                </div>
                <div className="text-xs text-white/70">
                  {dayjs(image.created_at).format('MMM DD, YYYY')}
                </div>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
};
